// viewer.playhead.js — persist the playback position per flight so a reload
// puts the heli (which follows the playhead) back where the reviewer left it.
//
// Same conventions as viewer.divider.js: localStorage may be blocked (HDD
// file:// shells, private browsing) — every access is try/caught and failure
// silently disables persistence. Values are validated strictly on read.

const KEY_PREFIX = "vmc.viewer.t.";

// A saved position this close to the end means the reviewer finished the
// flight — restart from 0 instead of restoring into the final seconds.
const END_GUARD_S = 5;

export function loadPlayhead(flightId, durationS) {
  if (!flightId) return null;
  let raw = null;
  try { raw = localStorage.getItem(KEY_PREFIX + flightId); } catch { return null; }
  if (typeof raw !== "string" || raw.trim() === "") return null;
  if (!/^\d+(?:\.\d+)?$/.test(raw)) return null;
  const t = Number(raw);
  if (!Number.isFinite(t) || t < 0) return null;
  if (Number.isFinite(durationS) && t >= durationS - END_GUARD_S) return null;
  return t;
}

export function savePlayhead(flightId, t) {
  if (!flightId || !Number.isFinite(t) || t < 0) return;
  try {
    localStorage.setItem(KEY_PREFIX + flightId, String(t));
  } catch { /* storage blocked — drop persistence silently */ }
}

// Throttled writer: timeupdate fires ~4 Hz; writing every event is wasteful.
// `now` is injectable for tests (Date.now, not performance.now — fake timers
// only patch the former).
export function createPlayheadSaver(flightId, { minIntervalMs = 3000, now = Date.now } = {}) {
  let lastWriteMs = -Infinity;
  return {
    onTimeUpdate(t) {
      const n = now();
      if (n - lastWriteMs < minIntervalMs) return;
      lastWriteMs = n;
      savePlayhead(flightId, t);
    },
    flush(t) {
      lastWriteMs = now();
      savePlayhead(flightId, t);
    },
  };
}
